<template>
  <div>
    <app-template>
      <div class="min-h-screen">

        <!-- HEADER -->
        <div class="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 class="text-2xl font-bold text-neutral-900 tracking-tight">Crédits SMS</h1>
            <p class="text-sm text-neutral-400 mt-0.5">Rechargez votre solde en quelques secondes.</p>
          </div>
          <div class="flex items-center gap-2 text-xs text-neutral-500 bg-neutral-100 border border-neutral-200 rounded-full px-4 py-2">
            <i class="fas fa-lock text-green-500"></i>
            <span>Paiements sécurisés</span>
            <span class="text-neutral-300">·</span>
            <i class="fab fa-stripe text-xl" style="color:#635bff;"></i>
          </div>
        </div>

        <!-- LOADER -->
        <div v-if="loading" class="flex flex-col items-center justify-center min-h-96 gap-3 text-neutral-400">
          <div class="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-orange-500 animate-spin"></div>
          <p class="text-sm">Chargement…</p>
        </div>

        <div v-else class="max-w-4xl mx-auto px-6 pb-12">

          <!-- SOLDE ACTUEL -->
          <div class="bg-neutral-900 text-white rounded-xl px-6 py-5 mb-8 flex items-center justify-between flex-wrap gap-4 shadow-xl">
            <div class="flex items-center gap-4">
              <div class="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <i class="fas fa-comment-dots text-orange-400"></i>
              </div>
              <div>
                <p class="text-xs font-mono uppercase tracking-widest text-white/30 mb-0.5">Solde actuel</p>
                <p class="text-2xl font-bold">{{ $account.account.societe.sms_credits }} <span class="text-base font-normal text-white/50">SMS</span></p>
              </div>
            </div>
            <span v-if="$account.account.societe.sms_credits > 0"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono bg-green-500/15 text-green-400 border border-green-500/20">
              <span class="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span> Crédit actif
            </span>
            <span v-else
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono bg-red-500/15 text-red-400 border border-red-500/20">
              <i class="fas fa-exclamation-triangle" style="font-size:10px;"></i> Aucun crédit
            </span>
          </div>

          <!-- ÉTAPE 1 — CHOISIR UN PACK -->
          <section v-if="step === 'packs'">
            <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-5 flex items-center gap-2">
              <span class="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">01</span>
              Choisissez un pack
            </p>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
              <button
                v-for="pack in packs"
                :key="pack.sms"
                type="button"
                class="text-left bg-white rounded-xl border-2 p-6 cursor-pointer transition-all duration-150 relative focus:outline-none"
                :class="[
                  selectedPack === pack.sms
                    ? 'border-orange-500 shadow-[0_0_0_3px_rgba(240,90,0,0.12)]'
                    : 'border-neutral-200 hover:border-neutral-300 hover:shadow-md',
                  pack.popular ? 'md:scale-[1.03]' : ''
                ]"
                @click="selectedPack = pack.sms"
              >
                <div v-if="pack.popular" class="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-neutral-900 text-white text-xs font-mono uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">
                  ⭐ Populaire
                </div>

                <div class="flex items-center justify-between mb-4">
                  <div class="w-9 h-9 rounded-lg flex items-center justify-center text-sm"
                    :class="pack.popular ? 'bg-neutral-900 text-orange-500' : 'bg-orange-50 text-orange-500'">
                    <i class="fas fa-comment-sms"></i>
                  </div>
                  <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center text-white transition-all"
                    :class="selectedPack === pack.sms ? 'bg-orange-500 border-orange-500' : 'border-neutral-300'">
                    <i v-if="selectedPack === pack.sms" class="fas fa-check" style="font-size:9px;"></i>
                  </div>
                </div>

                <p class="font-bold text-lg text-neutral-900">{{ pack.sms.toLocaleString('fr-FR') }} SMS</p>
                <p class="text-2xl font-bold text-neutral-900 mt-1">{{ pack.price }} <span class="text-sm font-normal text-neutral-400">€</span></p>
                <p class="text-xs text-neutral-400 mt-1 font-mono">{{ pack.unit }} ct / SMS</p>

                <div v-if="pack.saving" class="mt-3 inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full font-mono">
                  <i class="fas fa-tag" style="font-size:9px;"></i> {{ pack.saving }}
                </div>
              </button>
            </div>

            <button
              class="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg shadow-[0_4px_14px_rgba(240,90,0,0.3)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              :disabled="!selectedPack"
              @click="goToPayment"
            >
              Continuer <i class="fas fa-arrow-right"></i>
            </button>
          </section>

          <!-- ÉTAPE 2 — PAIEMENT -->
          <section v-if="step === 'payment'">

            <div class="flex items-center justify-between bg-white border border-neutral-200 rounded-xl px-5 py-3.5 mb-6 shadow-sm">
              <button class="flex items-center gap-2 text-sm text-neutral-400 hover:text-neutral-700 transition-colors" @click="step = 'packs'">
                <i class="fas fa-chevron-left"></i> Modifier
              </button>
              <div class="text-right">
                <span class="text-sm font-semibold text-neutral-900 block">{{ currentPack.sms.toLocaleString('fr-FR') }} SMS</span>
                <span class="text-xs font-mono text-neutral-400">{{ currentPack.price }} € HT</span>
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
              <div class="space-y-4">

                <!-- CB enregistrées -->
                <div v-if="paymentMethods.length" class="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
                  <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-4 flex items-center gap-2">
                    <span class="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">02</span>
                    Moyen de paiement
                  </p>
                  <div class="space-y-2.5">
                    <label v-for="pm in paymentMethods" :key="pm.id"
                      class="flex items-center justify-between border-2 rounded-lg px-4 py-3 cursor-pointer transition-all"
                      :class="selectedPmId === pm.id ? 'border-orange-500 bg-orange-50' : 'border-neutral-200 hover:border-neutral-300'">
                      <input type="radio" :value="pm.id" v-model="selectedPmId" class="absolute opacity-0 w-0 h-0" />
                      <div class="flex items-center gap-3">
                        <i :class="brandIcon(pm.brand)" class="text-2xl text-neutral-400"></i>
                        <div>
                          <p class="text-sm font-mono font-medium text-neutral-800">•••• •••• •••• {{ pm.last4 }}</p>
                          <p class="text-xs text-neutral-400">Expire {{ pm.exp_month }}/{{ pm.exp_year }}</p>
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <span v-if="pm.is_default" class="text-xs font-mono bg-neutral-100 text-neutral-400 px-2 py-0.5 rounded uppercase tracking-wide">Par défaut</span>
                        <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all"
                          :class="selectedPmId === pm.id ? 'bg-orange-500 border-orange-500' : 'border-neutral-300'">
                          <div v-if="selectedPmId === pm.id" class="w-1.5 h-1.5 rounded-full bg-white"></div>
                        </div>
                      </div>
                    </label>
                  </div>
                  <button
                    class="mt-4 w-full border-2 border-dashed border-neutral-200 hover:border-orange-400 hover:bg-orange-50 text-orange-500 text-sm py-2.5 rounded-lg flex items-center justify-center gap-2 transition-all"
                    @click="showNewCard = !showNewCard">
                    <i :class="showNewCard ? 'fas fa-minus' : 'fas fa-plus'"></i>
                    {{ showNewCard ? 'Annuler' : 'Utiliser une autre carte' }}
                  </button>
                </div>

                <!-- Formulaire carte Stripe -->
                <div v-show="!paymentMethods.length || showNewCard" class="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
                  <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-5 flex items-center gap-2">
                    <span class="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">{{ paymentMethods.length ? '—' : '02' }}</span>
                    {{ paymentMethods.length ? 'Nouvelle carte' : 'Carte bancaire' }}
                  </p>
                  <div class="space-y-4">
                    <div>
                      <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Numéro de carte</label>
                      <div ref="cardNumber" class="border border-neutral-200 rounded-lg px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Expiration</label>
                        <div ref="cardExpiry" class="border border-neutral-200 rounded-lg px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div>
                      </div>
                      <div>
                        <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">CVC</label>
                        <div ref="cardCvc" class="border border-neutral-200 rounded-lg px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div>
                      </div>
                    </div>
                    <div>
                      <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Nom du titulaire</label>
                      <input v-model="cardHolder" type="text"
                        class="w-full border border-neutral-200 rounded-lg px-4 py-3 bg-neutral-50 text-sm text-neutral-900 placeholder-neutral-300 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all"
                        placeholder="Jean Dupont" autocomplete="cc-name" />
                    </div>
                  </div>
                  <div class="mt-4 flex flex-wrap items-center gap-3 px-4 py-3 bg-neutral-50 border border-neutral-100 rounded-lg text-xs text-neutral-400 font-mono">
                    <span class="flex items-center gap-1.5"><i class="fas fa-lock text-green-500"></i> SSL 256 bits</span>
                    <span class="text-neutral-200">·</span>
                    <span class="flex items-center gap-1.5"><i class="fab fa-stripe" style="color:#635bff;"></i> Sécurisé par Stripe</span>
                    <span class="text-neutral-200">·</span>
                    <span class="flex items-center gap-1.5"><i class="fas fa-id-card text-blue-400"></i> 3D Secure</span>
                  </div>
                </div>

                <div v-if="errorMsg" class="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3.5 text-sm text-red-700">
                  <i class="fas fa-exclamation-triangle mt-0.5 shrink-0"></i>
                  <span>{{ errorMsg }}</span>
                </div>

                <button
                  class="w-full flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold py-4 rounded-xl shadow-[0_4px_14px_rgba(0,0,0,0.2)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="paying || !canPay"
                  @click="pay">
                  <span v-if="paying"><i class="fas fa-circle-notch fa-spin"></i>&nbsp; Traitement en cours…</span>
                  <span v-else><i class="fas fa-lock"></i>&nbsp; Payer {{ currentPack.price }} €</span>
                </button>

                <p class="text-center text-xs text-neutral-400 leading-relaxed">
                  En confirmant, vous acceptez nos <a href="https://storage.victoranw.fr/file/x3jvJaPzGU" target="_blank" class="text-orange-500 hover:underline">Conditions Générales d'Utilisation</a> et nos <a href="https://storage.victoranw.fr/file/ADo4JPI8RA" target="_blank" class="text-orange-500 hover:underline">Conditions Générales de Vente</a>.
                </p>
              </div>

              <!-- Récapitulatif -->
              <aside class="bg-neutral-900 text-white rounded-xl p-6 shadow-xl lg:sticky lg:top-6">
                <h3 class="font-bold text-base mb-5">Récapitulatif</h3>
                <div class="mb-1">
                  <div class="flex justify-between text-sm text-white/50 py-2 border-b border-white/5">
                    <span>{{ currentPack.sms.toLocaleString('fr-FR') }} SMS</span>
                    <span>{{ currentPack.price }} €</span>
                  </div>
                  <div class="flex justify-between text-sm text-white/50 py-2 border-b border-white/5">
                    <span>TVA (20 %)</span>
                    <span>{{ currentPack.price_tva }} €</span>
                  </div>
                  <div class="flex justify-between font-bold py-3">
                    <span>Total TTC</span>
                    <span>{{ currentPack.price_ttc }} €</span>
                  </div>
                </div>
                <div class="border-t border-white/10 my-4"></div>
                <div class="space-y-2.5">
                  <div class="flex items-center gap-2 text-xs text-white/40">
                    <i class="fas fa-check text-orange-400" style="font-size:10px;"></i>
                    <span>{{ currentPack.sms.toLocaleString('fr-FR') }} SMS crédités immédiatement</span>
                  </div>
                  <div class="flex items-center gap-2 text-xs text-white/40">
                    <i class="fas fa-infinity text-orange-400" style="font-size:10px;"></i>
                    <span>Crédits sans expiration</span>
                  </div>
                  <div class="flex items-center gap-2 text-xs text-white/40">
                    <i class="fas fa-file-alt text-orange-400" style="font-size:10px;"></i>
                    <span>Facture PDF par email</span>
                  </div>
                </div>
                <div class="border-t border-white/10 mt-4 pt-4 flex items-center gap-3">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Visa_Inc._logo_%282021%E2%80%93present%29.svg/3840px-Visa_Inc._logo_%282021%E2%80%93present%29.svg.png" alt="Visa" class="h-5 opacity-50" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/a/a4/Mastercard_2019_logo.svg" alt="Mastercard" class="h-4 opacity-40 invert" />
                  <i class="fab fa-stripe text-2xl opacity-50" style="color:#a5a3ff;"></i>
                </div>
              </aside>
            </div>
          </section>

          <!-- SUCCÈS -->
          <section v-if="step === 'success'" class="flex items-center justify-center min-h-96">
            <div class="bg-white border border-neutral-200 rounded-2xl p-12 max-w-md w-full text-center shadow-xl">
              <div class="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center text-white text-2xl mx-auto mb-6 shadow-[0_8px_24px_rgba(240,90,0,0.35)]">
                <i class="fas fa-check"></i>
              </div>
              <h2 class="text-2xl font-bold text-neutral-900 mb-3">Crédits ajoutés !</h2>
              <p class="text-sm text-neutral-400 leading-relaxed mb-2">
                <strong class="text-neutral-700">{{ currentPack.sms.toLocaleString('fr-FR') }} SMS</strong> ont été crédités sur votre compte.
              </p>
              <p class="text-sm text-neutral-400 mb-8">
                Nouveau solde : <strong class="text-neutral-700">{{ newBalance }} SMS</strong>
              </p>
              <button
                class="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-all shadow-[0_4px_14px_rgba(240,90,0,0.3)]"
                @click="reset">
                Acheter d'autres crédits <i class="fas fa-arrow-right"></i>
              </button>
            </div>
          </section>

        </div>
      </div>
    </app-template>
  </div>
</template>

<script>
/* global Stripe */
import env from '@/services/env.service'

const STRIPE_STYLE = {
  base: {
    fontSize: '15px',
    color: '#1a1a1a',
    fontSmoothing: 'antialiased',
    '::placeholder': { color: '#bbbbbb' },
  },
  invalid: { color: '#c0392b', iconColor: '#c0392b' },
}

export default {
  name: 'ParametreSms',

  data () {
    return {
      loading: false,
      step: 'packs', // packs | payment | success

      packs: [
        {
          sms: 500,
          price: '46,80',
          price_tva: '0',
          price_ttc: '46,80',
          price_amount: 3900,
          unit: '7,8',
          popular: false,
          saving: null,
        },
        {
          sms: 2000,
          price: '168',
          price_tva: '0',
          price_ttc: '168,00',
          price_amount: 14000,
          unit: '7,0',
          popular: true,
          saving: '-10 % vs pack 500',
        },
        {
          sms: 10000,
          price: '720',
          price_tva: '0',
          price_ttc: '720,00',
          price_amount: 60000,
          unit: '6,0',
          popular: false,
          saving: '-23 % vs pack 500',
        },
      ],

      selectedPack: null,
      newBalance: 0,

      // Stripe
      stripe: null,
      elements: null,
      elNumber: null,
      elExpiry: null,
      elCvc: null,
      cardHolder: '',
      stripeReady: false,

      // Cartes
      paymentMethods: [],
      selectedPmId: null,
      showNewCard: false,
      loadingCards: false,

      // UI
      paying: false,
      errorMsg: null,
    }
  },

  computed: {
    societe () {
      return this.$account?.account?.societe || {}
    },
    currentPack () {
      return this.packs.find(p => p.sms === this.selectedPack) || this.packs[0]
    },
    canPay () {
      if (this.paymentMethods.length && !this.showNewCard && this.selectedPmId) return true
      return this.stripeReady
    },
  },

  methods: {
    async goToPayment () {
      this.step = 'payment'
      this.errorMsg = null
      this.stripeReady = false
      await this.loadCards()
      await this.$nextTick()
      await this.$nextTick()
      await this.loadStripe()
      this.mountPaymentElements()
    },

    reset () {
      this.step = 'packs'
      this.selectedPack = null
      this.showNewCard = false
      this.errorMsg = null
    },

    // Stripe
    async loadStripe () {
      if (this.stripe) return
      const pk = env.stripeKey
      if (!pk) {
        this.errorMsg = 'Configuration Stripe manquante.'
        throw new Error('Stripe PK manquante')
      }
      if (!window.Stripe) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script')
          s.src = 'https://js.stripe.com/v3/'
          s.onload = resolve
          s.onerror = () => reject(new Error('Impossible de charger Stripe.js'))
          document.head.appendChild(s)
        })
      }
      this.stripe   = Stripe(pk)
      this.elements = this.stripe.elements()
    },

    mountPaymentElements () {
      ;[this.elNumber, this.elExpiry, this.elCvc].forEach(el => {
        if (el) { try { el.destroy() } catch (_) {} }
      })

      const numEl = this.$refs.cardNumber
      const expEl = this.$refs.cardExpiry
      const cvcEl = this.$refs.cardCvc

      if (!numEl || !expEl || !cvcEl) {
        setTimeout(() => this.mountPaymentElements(), 100)
        return
      }

      this.elNumber = this.elements.create('cardNumber', { style: STRIPE_STYLE, showIcon: true })
      this.elExpiry = this.elements.create('cardExpiry', { style: STRIPE_STYLE })
      this.elCvc    = this.elements.create('cardCvc',    { style: STRIPE_STYLE })

      numEl.innerHTML = ''
      expEl.innerHTML = ''
      cvcEl.innerHTML = ''

      this.elNumber.mount(numEl)
      this.elExpiry.mount(expEl)
      this.elCvc.mount(cvcEl)

      this.elNumber.on('ready', () => { this.stripeReady = true })
      this.elNumber.on('change', e => { this.errorMsg = e.error?.message || null })
    },

    // API
    async loadCards () {
      if (!this.societe?.id) return
      this.loadingCards = true
      try {
        const res = await this.$request.get(`/stripe/payment-methods/${this.societe.id}`)
        this.paymentMethods = res.payment_methods || []
        const def = this.paymentMethods.find(p => p.is_default)
        this.selectedPmId = def?.id || this.paymentMethods[0]?.id || null
      } catch (e) {
        console.error('loadCards', e)
      } finally {
        this.loadingCards = false
      }
    },

    async ensureCustomer () {
      if (this.societe.stripe_id) return true
      try {
        await this.$request.post('/stripe/customers', { societe_id: this.societe.id })
        return true
      } catch {
        this.errorMsg = 'Impossible de créer votre profil de paiement.'
        return false
      }
    },

    async saveCard () {
      try {
        const siRes = await this.$request.post('/stripe/setup-intent', { societe_id: this.societe.id })
        const { setupIntent, error } = await this.stripe.confirmCardSetup(siRes.client_secret, {
          payment_method: {
            card: this.elNumber,
            billing_details: { name: this.cardHolder || this.societe.raison_sociale || '' },
          },
        })
        if (error) { this.errorMsg = error.message; return null }
        await this.$request.post('/stripe/payment-methods/attach', {
          societe_id:        this.societe.id,
          payment_method_id: setupIntent.payment_method,
          set_as_default:    true,
        })
        return setupIntent.payment_method
      } catch (e) {
        this.errorMsg = e?.response?.data?.message || "Erreur lors de l'enregistrement de la carte."
        return null
      }
    },

    async pay () {
      this.errorMsg = null
      this.paying = true
      try {
        if (!await this.ensureCustomer()) return

        let pmId = this.selectedPmId
        if (!pmId || this.showNewCard) {
          pmId = await this.saveCard()
          if (!pmId) return
        }

        // Paiement + crédit SMS en une seule route
        const res = await this.$request.post('/stripe/sms/buy-credits', {
          societe_id:        this.societe.id,
          sms_pack:          this.currentPack.sms,
          amount:            this.currentPack.price_amount,
          currency:          'eur',
          description:       `Pack ${this.currentPack.sms} SMS`,
          payment_method_id: pmId,
          taux_tva:          20,
        })

        // Mise à jour locale du solde
        if (this.$account?.account?.societe) {
          this.$account.account.societe.sms_credits = res.new_balance
        }
        this.newBalance = res.new_balance

        this.step = 'success'
      } catch (e) {
        this.errorMsg = e?.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.'
      } finally {
        this.paying = false
      }
    },

    brandIcon (brand) {
      const b = (brand || '').toLowerCase()
      if (b === 'visa')       return 'fab fa-cc-visa'
      if (b === 'mastercard') return 'fab fa-cc-mastercard'
      if (b === 'amex')       return 'fab fa-cc-amex'
      if (b === 'discover')   return 'fab fa-cc-discover'
      return 'fas fa-credit-card'
    },
  },

  beforeDestroy () {
    ;[this.elNumber, this.elExpiry, this.elCvc].forEach(el => {
      if (el) { try { el.destroy() } catch (_) {} }
    })
  },
}
</script>